/**
 * Readers over `ModelOutput.attachedImages` — the record of what images an LLM
 * call carried, in send order.
 *
 * The badges below used to read two separate fields, `attachedImage` (a boolean
 * that meant "the STUDENT's image went out") and `attachedQuestionImages` (a
 * label list). Roles carry that distinction now, so every question a reader
 * asks is a filter over one list.
 *
 * `attachedImages` is absent on rows written before image recording; the
 * helpers report "no images", and callers that need to tell "none" from
 * "unknown" should test the field itself.
 */
import type { AttachedImage } from '../types';

/** Did the student's own work go out with this call? Question diagrams and
 *  page scans do not count — that has always been the badge's meaning. */
export const sentStudentImage = (images?: AttachedImage[]): boolean =>
    !!images?.some(i => i.role === 'student');

/** The label the student's image was sent under, when it carried one. Emitted
 *  by the adapter as a text part next to the image, so it appears nowhere in
 *  the prompt body. */
export const studentImageLabel = (images?: AttachedImage[]): string | undefined =>
    images?.find(i => i.role === 'student')?.label;

/** Was this particular question/part diagram among the images sent? */
export const sentQuestionImage = (images: AttachedImage[] | undefined, label: string): boolean =>
    !!images?.some(i => i.role === 'question' && i.label === label);

/**
 * The student images a run sent the model, in order, as loadable URLs — what
 * every view of that run must show, and nothing else.
 *
 * Runs since 30 Oct 2026 record each image's `path`. An older run recorded
 * only how many it sent, and its own ModelInput names what it loaded: its
 * pages when it sent as many as there are (`inputPageUrls`), or its one image
 * when it has no pages (`inputImageUrl`). Anything else cannot be known, so
 * nothing is returned rather than a guess.
 */
export const sentStudentImagesOf = (
    images: AttachedImage[] | undefined,
    inputImageUrl: string | undefined,
    inputPageUrls: string[] | undefined,
): { url: string; label?: string }[] => {
    const student = (images ?? []).filter(i => i.role === 'student');
    if (student.length > 0 && student.every(i => !!i.path)) {
        return student.map(i => ({ url: i.path!, ...(i.label ? { label: i.label } : {}) }));
    }
    const pages = inputPageUrls ?? [];
    if (pages.length > 1 && student.length === pages.length) return pages.map(url => ({ url }));
    if (student.length === 1 && inputImageUrl && pages.length < 2) return [{ url: inputImageUrl }];
    return [];
};
